import {
  Button,
  InputAdornment,
  TextField,
  IconButton,
  Autocomplete, // ✅ NEW
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { MdSearch } from "react-icons/md";
import CloseIcon from "@mui/icons-material/Close";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";

const PreviewTable = ({
  previewTableData, // All data from parent
  loading,
  onExport,
  fileType,
  handleChildData,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [allData, setAllData] = useState([]); // Store all data for frontend pagination
  const [totalItems, setTotalItems] = useState(0);

  // ✅ NEW: Dropdown filter states
  const [selectedHistory, setSelectedHistory] = useState("");
  const [selectedFileType, setSelectedFileType] = useState("");
  const [selectedFileName, setSelectedFileName] = useState("");
  const [selectedScenario, setSelectedScenario] = useState("");

  // Sorting
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const getSortableFields = () => {
    // Map file types to sortable fields based on requirements
    if (fileType === "OFAC_SDN" || fileType === "OFAC") {
      return ["ENT_NUM", "SDN_NAME", "SDN_TYPE", "SCENARIO_NAME"];
    } else if (fileType === "UK_Sanctions" || fileType === "UKSanctions") {
      return ["UNIQUE_ID", "NAME", "TYPE", "SCENARIO_NAME"];
    } else if (fileType === "OFAC_NON_SDN" || fileType === "NONSDN") {
      return ["ENT_NUM", "SDN_NAME", "SDN_TYPE", "SCENARIO_NAME"];
    } else if (fileType === "PEP") {
      return ["NAME", "SCENARIO_NAME"];
    } else if (fileType === "WATCH_LIST" || fileType === "Watch List") {
      return ["NAME", "SCENARIO_NAME"];
    } else if (
      fileType === "NC" ||
      fileType === "Negative Cases" ||
      fileType === "Negative"
    ) {
      return ["NAME", "TYPE"];
    } else if (fileType === "PC-LFM") {
      return ["NAME"];
    } else if (fileType === "WHITE_LIST" || fileType === "White List") {
      return ["NAME"];
    }
    // Default: allow sorting on common fields
    return ["ENT_NUM", "NAME", "SCENARIO_NAME"];
  };

  // Add handleSort function (after getSortableFields)
  const handleSort = (columnKey) => {
    const sortableFields = getSortableFields();
    const normalizedKey = columnKey.toUpperCase().replace(/\s+/g, "_");

    if (!sortableFields.includes(normalizedKey)) {
      return;
    }

    setSortConfig((prevConfig) => {
      if (prevConfig.key === normalizedKey) {
        return {
          key: normalizedKey,
          direction: prevConfig.direction === "asc" ? "desc" : "asc",
        };
      } else {
        return {
          key: normalizedKey,
          direction: "asc",
        };
      }
    });
    setCurrentPage(1);
  };

  //  Add renderSortIcon function (after handleSort)
  const renderSortIcon = (columnKey) => {
    const normalizedKey = columnKey.toUpperCase().replace(/\s+/g, "_");
    const sortableFields = getSortableFields();

    if (!sortableFields.includes(normalizedKey)) {
      return null;
    }

    const isActive = sortConfig.key === normalizedKey;
    const isAsc = sortConfig.direction === "asc";

    if (!isActive)
      return (
        <FaSort
          className="sort-icon inactive"
          style={{ marginLeft: 4, opacity: 0.5 }}
        />
      );

    return isAsc ? (
      <FaSortUp className="sort-icon active" style={{ marginLeft: 4 }} />
    ) : (
      <FaSortDown className="sort-icon active" style={{ marginLeft: 4 }} />
    );
  };

  // Update allData when previewTableData changes
  useEffect(() => {
    if (previewTableData && previewTableData.length > 0) {
      setAllData(previewTableData);
      setTotalItems(previewTableData.length);
      setCurrentPage(1); // Reset to first page when new data arrives
      setSortConfig({ key: null, direction: "asc" }); // Reset sorting

      // ✅ Also reset dropdown filters when new data comes
      setSelectedHistory("");
      setSelectedFileType("");
      setSelectedFileName("");
      setSelectedScenario("");
    } else {
      setAllData([]);
      setTotalItems(0);
    }
  }, [previewTableData]);

  // Implement same rules as ViewFilesModal.filterModalData:
  // 1) ENT_NUM (unique_id) exact match (case-insensitive)
  // 2) NAME (original_name) partial match (case-insensitive)
  // 3) scenario exact match (case-insensitive)
  // Explicitly DO NOT search processed_name
  const matchesSearch = (row, rawSearch) => {
    if (!rawSearch) return true;
    const search = rawSearch.trim();
    if (!search) return true;
    const searchLower = search.toLowerCase();

    try {
      // 1. ENT_NUM exact match
      if (
        row.unique_id &&
        String(row.unique_id).toLowerCase() === searchLower
      ) {
        return true;
      }

      // 2. NAME partial match
      if (
        row.original_name &&
        String(row.original_name).toLowerCase().includes(searchLower)
      ) {
        return true;
      }

      // 3. scenario exact match
      if (row.scenario && String(row.scenario).toLowerCase() === searchLower) {
        return true;
      }
    } catch (e) {
      return false;
    }

    return false;
  };

  // ✅ Helper: apply all filters (search + 3 dropdowns)
  const applyAllFilters = (data) => {
    if (!data || data.length === 0) return [];

    let filteredData = data;

    // 1) Search box
    if (searchTerm && searchTerm.trim()) {
      filteredData = filteredData.filter((row) =>
        matchesSearch(row, searchTerm)
      );
    }

    // 2) current_history dropdown
    if (selectedHistory) {
      filteredData = filteredData.filter(
        (row) =>
          String(row.current_history || "").toLowerCase() ===
          String(selectedHistory).toLowerCase()
      );
    }

    // 3) file_type dropdown
    if (selectedFileType) {
      filteredData = filteredData.filter(
        (row) =>
          String(row.file_type || "").toLowerCase() ===
          String(selectedFileType).toLowerCase()
      );
    }

    // 4) file_name dropdown
    if (selectedFileName) {
      filteredData = filteredData.filter(
        (row) =>
          String(row.file_name || "").toLowerCase() ===
          String(selectedFileName).toLowerCase()
      );
    }
   // 5) scenario dropdown
    if (selectedScenario) {
      console.log("======",filteredData)
      filteredData = filteredData.filter(
        (row) =>
          String(row.scenario || "").toLowerCase() ===
          String(selectedScenario).toLowerCase()
      );
      console.log("=++++++++++=====",filteredData)
    }
  
    return filteredData;
  };

  const getFilteredData = () => {
    if (!allData || allData.length === 0) return [];

    // 👉 First apply search + dropdown filters
    let filteredData = applyAllFilters(allData);

    // 👉 Then apply sorting
    if (sortConfig.key) {
      filteredData = [...filteredData].sort((a, b) => {
        // Map column keys to data keys
        const getValue = (row, key) => {
          // Handle different key formats
          const keyMappings = {
            ENT_NUM: ["unique_id", "ent_num", "ENT_NUM"],
            SDN_NAME: ["original_name", "sdn_name", "SDN_NAME", "name"],
            SDN_TYPE: ["sdn_type", "SDN_TYPE", "type"],
            NAME: ["original_name", "name", "NAME"],
            UNIQUE_ID: ["unique_id", "UNIQUE_ID", "ENT_NUM"],
            TYPE: ["type", "TYPE"],
            SCENARIO_NAME: [
              "scenario",
              "scenario_name",
              "SCENARIO_NAME",
              "SCENARIO",
            ],
            PROCESSED_NAME: ["processed_name", "PROCESSED_NAME"],
            PRE_PROCESSED_NAME: ["pre_processed_name", "PRE_PROCESSED_NAME"],
          };

          const possibleKeys = keyMappings[key] || [
            key.toLowerCase(),
            key,
            key.toUpperCase(),
          ];

          for (const possibleKey of possibleKeys) {
            if (row[possibleKey] !== undefined && row[possibleKey] !== null) {
              return row[possibleKey];
            }
          }
          return "";
        };

        const aValue = getValue(a, sortConfig.key);
        const bValue = getValue(b, sortConfig.key);

        // Handle numeric values
        const aNum = parseFloat(aValue);
        const bNum = parseFloat(bValue);
        const isNumeric =
          !isNaN(aNum) && !isNaN(bNum) && isFinite(aNum) && isFinite(bNum);

        let comparison = 0;
        if (isNumeric) {
          comparison = aNum - bNum;
        } else {
          const aStr = String(aValue || "").toLowerCase();
          const bStr = String(bValue || "").toLowerCase();
          comparison = aStr.localeCompare(bStr);
        }

        return sortConfig.direction === "asc" ? comparison : -comparison;
      });
    }

    // Then apply pagination
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  };

  // Handle search input
  const handleSearch = (event) => {
    const newSearchTerm = event.target.value;
    setSearchTerm(newSearchTerm);

    // Reset to first page when searching
    if (newSearchTerm !== searchTerm) {
      setCurrentPage(1);
    }
  };

  // Pagination handlers
  const handleFirstPage = () => setCurrentPage(1);
  const handleLastPage = () =>
    setCurrentPage(Math.ceil(totalItems / itemsPerPage));
  const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNextPage = () =>
    setCurrentPage((prev) =>
      Math.min(prev + 1, Math.ceil(totalItems / itemsPerPage))
    );

  // Add a useEffect to update total items count when search or data changes
  useEffect(() => {
    if (!allData || allData.length === 0) {
      setTotalItems(0);
      // 🔥 send empty data to parent
      handleChildData([]);
      return;
    }

    const filteredData = applyAllFilters(allData);
    setTotalItems(filteredData.length);
    handleChildData(filteredData);
  }, [
    allData,
    searchTerm,
    selectedHistory,
    selectedFileType,
    selectedFileName,
    selectedScenario,
  ]);

  // ✅ Helper: get unique options from response
  const getUniqueOptions = (field) =>
    Array.from(
      new Set(
        (allData || [])
          .map((row) => row[field])
          .filter((val) => val !== undefined && val !== null && val !== "")
      )
    );

  // 👉 Prepare options for the three dropdowns
  const historyOptions = getUniqueOptions("current_history");
  const fileTypeOptions = getUniqueOptions("file_type");
  const fileNameOptions = getUniqueOptions("file_name");
  const scenarioOptions = getUniqueOptions("scenario");

  return (
    <div className="mt-3">
      <div>
        {!loading && (
          <>
            <div className="modal-header">
              <h5>Preview Data</h5>
            </div>
            <div className="modal-body">
              {/* <div className="d-flex justify-content-between align-items-center flex-wrap gap-2"> */}
              <div className="d-flex flex-wrap gap-2 mt-3">
                {/* <div className="justify-content-start"> */}
                <div style={{ flex: "1 1 0", minWidth: 220 }}>
                  <TextField
                    className="w-100"
                    size="small"
                    variant="outlined"
                    placeholder="Search by Name"
                    value={searchTerm || ""}
                    onChange={handleSearch}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MdSearch />
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSearchTerm("");
                              setCurrentPage(1);
                            }}
                            edge="end"
                          >
                            <CloseIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
                <div style={{ flex: "1 1 0", minWidth: 220 }}></div>
                <div style={{ flex: "1 1 0", minWidth: 220 }}></div>

                <div style={{ flex: "1 1 0", minWidth: 220 }}>
                  <div className="d-flex justify-content-end">
                    {typeof onExport === "function" && (
                      <Button
                        variant="contained"
                        color="primary"
                        style={{
                          backgroundColor: "var(--theme-color)",
                          color: "#fff",
                          whiteSpace: "nowrap",
                        }}
                        onClick={onExport}
                      >
                        Export
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* 🔹 BOTTOM ROW: THREE SEARCHABLE DROPDOWNS */}
              <div className="d-flex flex-wrap gap-2 mt-3">
                <div style={{ flex: "1 1 0", minWidth: 220 }}>
                  {/* current_history */}
                  <Autocomplete
                    size="small"
                    options={historyOptions}
                    value={selectedHistory || null}
                    onChange={(_, newValue) => {
                      setSelectedHistory(newValue || "");
                      setCurrentPage(1);
                    }}
                    clearOnEscape
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Current / History"
                        // placeholder="Select current/history"
                        variant="outlined"
                        style={{ minWidth: 220 }}
                      />
                    )}
                  />
                </div>
                <div style={{ flex: "1 1 0", minWidth: 220 }}>
                  {/* file_type */}
                  <Autocomplete
                    size="small"
                    options={fileTypeOptions}
                    value={selectedFileType || null}
                    onChange={(_, newValue) => {
                      setSelectedFileType(newValue || "");
                      setCurrentPage(1);
                    }}
                    clearOnEscape
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="File Type"
                        // placeholder="Select file type"
                        variant="outlined"
                        style={{ minWidth: 220 }}
                      />
                    )}
                  />
                </div>
                <div style={{ flex: "1 1 0", minWidth: 220 }}>
                  {/* file_name */}
                  <Autocomplete
                    size="small"
                    options={fileNameOptions}
                    value={selectedFileName || null}
                    onChange={(_, newValue) => {
                      setSelectedFileName(newValue || "");
                      setCurrentPage(1);
                    }}
                    clearOnEscape
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="File Name"
                        // placeholder="Select file name"
                        variant="outlined"
                        style={{ minWidth: 220 }}
                      />
                    )}
                  />
                </div>
                  <div style={{ flex: "1 1 0", minWidth: 220 }}>
                  {/* file_name */}
                  <Autocomplete
                    size="small"
                    options={scenarioOptions}
                    value={selectedScenario || null}
                    onChange={(_, newValue) => {
                      setSelectedScenario(newValue || "");
                      setCurrentPage(1);
                    }}
                    clearOnEscape
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Scenario Name"
                        // placeholder="Select file name"
                        variant="outlined"
                        style={{ minWidth: 220 }}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </>
        )}

        <div className="table-responsive mt-3">
          {loading ? (
            <div className="text-center py-4">
              <p>Loading preview data...</p>
            </div>
          ) : getFilteredData().length > 0 ? (
            <table className="table table-bordered table-striped">
              <thead className="table-dark">
                <tr>
                  {[
                    fileType === "OFAC_SDN" ||
                    fileType === "OFAC_NON_SDN" ||
                    fileType === "OFAC" ||
                    fileType === "NONSDN"
                      ? "ENT_NUM"
                      : "ENT_NUM",
                    "Name",
                    "Pre Processed Name",
                    "Processed Name",
                    "Scenario Name",
                  ].map((header, index) => {
                    const normalizedKey = header
                      .toUpperCase()
                      .replace(/\s+/g, "_");
                    const sortableFields = getSortableFields();
                    const isSortable =
                      sortableFields.includes(normalizedKey) ||
                      (sortableFields.includes("ENT_NUM") &&
                        header === "ENT NUM") ||
                      (sortableFields.includes("NAME") && header === "Name") ||
                      (sortableFields.includes("SCENARIO_NAME") &&
                        header === "Scenario Name") ||
                      (sortableFields.includes("TYPE") && header === "Type");

                    // Map display headers to sort keys
                    const headerToKeyMap = {
                      "ENT NUM": "ENT_NUM",
                      Name:
                        fileType === "UK_Sanctions"
                          ? "NAME"
                          : fileType === "OFAC_SDN" ||
                            fileType === "OFAC_NON_SDN"
                          ? "SDN_NAME"
                          : "NAME",
                      "Pre Processed Name": "PRE_PROCESSED_NAME",
                      "Processed Name": "PROCESSED_NAME",
                      "Scenario Name": "SCENARIO_NAME",
                      Type: "TYPE",
                    };

                    const sortKey = headerToKeyMap[header] || normalizedKey;
                    const isActuallySortable = sortableFields.includes(sortKey);

                    return (
                      <th
                        key={index}
                        onClick={() =>
                          isActuallySortable && handleSort(sortKey)
                        }
                        style={{
                          cursor: isActuallySortable ? "pointer" : "default",
                          userSelect: "none",
                          position: "relative",
                        }}
                        className="sortable-header"
                      >
                        {header}
                        {isActuallySortable && renderSortIcon(sortKey)}
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {getFilteredData().map((row, index) => (
                  <tr key={index}>
                    <td>{row.unique_id || "--"}</td>
                    <td>{row.original_name || "--"}</td>
                    <td>{row.pre_processed_name || "--"}</td>
                    <td>{row.processed_name || "--"}</td>
                    <td>{row.scenario || "--"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="table table-bordered table-striped">
              <thead className="table-dark">
                <tr>
                  <th>ENT NUM</th>
                  <th>Name</th>
                  <th>Processed Name</th>
                  <th>Pre Processed Name</th>
                  <th>Scenario</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-center" colSpan={5}>
                    No data to preview.
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>

        {/* Main table pagination */}
        {!loading && getFilteredData().length > 0 && (
          <div className="d-flex justify-content-end mt-3">
            <div className="pagination">
              <Button
                variant="outlined"
                size="small"
                onClick={handleFirstPage}
                disabled={currentPage === 1}
                className="me-2"
              >
                First
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className="me-2"
              >
                Prev
              </Button>
              <span className="mx-2 align-self-center">
                Page {currentPage} of{" "}
                {Math.max(1, Math.ceil(totalItems / itemsPerPage))}
              </span>
              <Button
                variant="outlined"
                size="small"
                onClick={handleNextPage}
                disabled={
                  currentPage === Math.ceil(totalItems / itemsPerPage) ||
                  totalItems === 0
                }
                className="me-2"
              >
                Next
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleLastPage}
                disabled={
                  currentPage === Math.ceil(totalItems / itemsPerPage) ||
                  totalItems === 0
                }
              >
                Last
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PreviewTable;
